import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Container, Typography, Box, Paper, Button, CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import logo from '../assets/images/logo.png';
import Ratings from '../components/Ratings';
import { getURL } from '../utils/config';
import GaugeChart from 'react-gauge-chart';
import { format, parseISO } from "date-fns";

function Dashboard({ onLogout }) {
  const inputRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const googleReviewsRef = useRef(null);
  const crimeRadiusRef = useRef(null);
  // const crimeRateRef = useRef(null);
  // const zipCrimeRef = useRef(null);
  const socioRef = useRef(null);
  const businessRef = useRef(null);
  //const sexOffenseRef = useRef(null);

  const [placeDetails, setPlaceDetails] = useState(null);
  const [scores, setScores] = useState(null);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [percentValue, setPercentValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [expandAll, setExpandAll] = useState(null);
  const [scoreDetails, setScoreDetails] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyList, setHistoryList] = useState([]);

  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${getURL}/history`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const result = await response.json();
      return result;
    } catch (err) {
      console.error('Error fetching history:', err);
      return [];
    }
  };

  const getHistory = async () => {
    const data = await fetchHistory();
    setHistoryList(data);
    setHistoryOpen(true);
  };

  const handleHistoryClose = () => setHistoryOpen(false);

  const handleHistoryClick = (property) => {
    const placeInfo = {
      'place_id': property.PropertyId,
      'property_name': property.PropertyName,
      'address': property.Address,
      'lat': property.Latitude,
      'lng': property.Longitude,
      'zip': property.Zipcode
    };
    if (inputRef.current) {
      inputRef.current.value = property.PropertyName || '';
    }
    setHistoryOpen(false);
    setPlaceDetails(placeInfo);
    setRefreshFlag(0);
  };

  const fetchData = useCallback(async (refreshStatus) => {
    if (!placeDetails) return;
    setLoading(true);
    setExpandAll(false);
    setScores(null);
    setPropertyDetails(null);
    setFinalScore(0);
    setPercentValue(0);

    const details = { ...placeDetails, IsNeedRefresh: refreshStatus };

    try {
      const response = await fetch(`${getURL}/property-details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(details),
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const result = await response.json();
      if (result.status === "success") {
        const data = result.data;
        setScores({
          googleReviews: data.googleReviews,
          crimeRadius: data.crimeRadius,
          zipCodeCrimeGrade: data.zipCodeCrimeGrade,
          localSocialConditions: data.localSocialConditions,
          nearbyBusinessRisks: data.nearbyBusinessRisks,
          sexOffenders: data.sexOffenders
        });

        const overallScore = data.overallScore;
        //const overallScore = 10;
        setFinalScore(overallScore);
        setPercentValue((overallScore - 0.5) / 10);
        setPropertyDetails(data.propertyInfo);
        setLastUpdated(data.last_updated);
        setScoreDetails(propertyScoreDetails(data.propertyInfo?.GoogleReviewResponse?.user_ratings_total));
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [placeDetails]);

  useEffect(() => {
    if (!placeDetails) return;
    fetchData(refreshFlag);
  }, [placeDetails, refreshFlag, fetchData]);

  useEffect(() => {
    if (!apiKey) {
      console.error('Google API Key missing.');
      return;
    }
    const existingScript = document.getElementById('googleMaps');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
      script.id = 'googleMaps';
      script.async = true;
      script.defer = true;
      script.onload = () => setApiLoaded(true);
      document.body.appendChild(script);
    } else {
      setApiLoaded(true);
    }
  }, [apiKey]);

  useEffect(() => {
    if (!apiLoaded) return;
    const waitForGoogle = setInterval(() => {
      if (window.google?.maps?.places && inputRef.current) {
        clearInterval(waitForGoogle);
        const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current);
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (!place.geometry) return;
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const address = place.formatted_address;
          const place_id = place.place_id;
          const zipComp = place.address_components.find(c => c.types.includes('postal_code'));
          const zip = zipComp ? zipComp.long_name : 'N/A';
          const property_name = inputRef.current.value;
          setPlaceDetails({ place_id, property_name, address, lat, lng, zip });
          setRefreshFlag(0);
        });
      }
    }, 300);
    return () => clearInterval(waitForGoogle);
  }, [apiLoaded]);

  const handleRefresh = () => {
    setRefreshFlag(1);
  };

  const propertyScoreDetails = (count) => (
    <Box>
      <Typography variant="body2">Final Risk Score = </Typography>
      {count > 0 &&
        <Typography variant="body2">(0.5 × Google Reviews) +</Typography>
      }
      <Typography variant="body2">(0.35 × Crime Radius) +</Typography>
      {/* <Typography variant="body2">(0.1 × Zip Code Crime Grade) +</Typography> */}
      <Typography variant="body2">(0.2 × Local Socioeconomic Conditions) +</Typography>
      <Typography variant="body2">(0.05 × Nearby Business Risk)</Typography>
      <Typography variant="body2" mt={1}>
        <strong>Round the result to the nearest whole number.</strong>
      </Typography>
    </Box>
  );

  useEffect(() => {
    if (!placeDetails || !mapRef.current || !window.google) return;
    const { lat, lng, address, place_id, zip } = placeDetails;
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat, lng },
      zoom: 16,
    });
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }
    markerRef.current = new window.google.maps.Marker({
      position: { lat, lng },
      map,
      title: address,
    });
    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="font-size: 15px;">
          <div style="font-weight: bold;">${address}</div>
          <div>Place ID: ${place_id}</div>
          <div>Latitude: ${lat}</div>
          <div>Longitude: ${lng}</div>
          <div>Zip Code: ${zip}</div>
        </div>`,
    });
    markerRef.current.addListener('click', () => {
      infoWindow.open(map, markerRef.current);
    });
  }, [placeDetails]);

  const handleToggleExpandAll = () => {
    setExpandAll(prev => (prev === true ? false : true));
  };

  const getRiskLevel = () => {
    if (finalScore >= 8) return 'High Risk';
    if (finalScore >= 5) return 'Medium Risk';
    return 'Low Risk';
  };

  const scrollToRef = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Container maxWidth="xl" sx={{ pb: 1 }}>
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        mb={2}
        gap={{ xs: 2, sm: 0 }}
      >
        <Box
          width={{ xs: "100%", sm: "33.33%" }}
          display="flex"
          justifyContent={{ xs: "center", sm: "flex-start" }}
          alignItems="center"
          mb={{ xs: 1, sm: 0 }}
        >
          <img
            src={logo}
            alt="Logo"
            style={{
              height: 70,
              maxWidth: "100%",
              objectFit: "contain"
            }}
          />
        </Box>
        <Box
          width={{ xs: "100%", sm: "33.33%" }}
          textAlign="center"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mb={{ xs: 1, sm: 0 }}
        >
          <Typography variant="h6" sx={{ fontSize: { xs: 16, sm: 20 } }}>
            VIDISKY - Property Safety & Risk Report
          </Typography>
        </Box>
        <Box
          width={{ xs: "100%", sm: "33.33%" }}
          display="flex"
          justifyContent={{ xs: "center", sm: "flex-end" }}
          alignItems="center"
        >
          <Button
            variant="outlined"
            onClick={onLogout}
            sx={{ minWidth: 100 }}
          >
            Logout
          </Button>
        </Box>
      </Box>
      <Paper elevation={2} sx={{ p: { xs: 1, md: 2 }, borderRadius: 3 }}>
        <Box mb={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h6" gutterBottom>
              Search Address
            </Typography>
            <Button variant="contained" size="small" onClick={getHistory}>
              History
            </Button>
          </Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Enter the Address"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              marginBottom: 16
            }}
          />
          </Box>
        </Box>

      
      
        {!loading && placeDetails && scores && (
          <Box mt={3} display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
            <Box flex={1.2} bgcolor="#e6e6fa" borderRadius={3} boxShadow={3} px={{ xs: 2, md: 4 }} py={{ xs: 1, md: 3 }}  sx={{ mb: 4 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
                mb={2}
                py={1}
              >
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: { xs: "row", sm: "row" },
                    flexWrap: "nowrap",
                    flex: { xs: "1 1 100%", sm: "1 1 auto" },
                    minWidth: 0,
                    whiteSpace: { xs: "nowrap", sm: "normal" },
                    overflow: "visible",
                    fontSize: { xs: "1.1rem", sm: "1.25rem" }
                  }}
                >
                  Last Updated:&nbsp;
                  <span style={{ fontWeight: 600, color: "#1976d2", whiteSpace: "nowrap", fontSize: "inherit" }}>
                    {lastUpdated
                      ? format(
                          parseISO(lastUpdated),
                          "MM/dd/yyyy hh:mm:ss a"
                        )
                      : ""}
                  </span>
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleRefresh}
                  sx={{
                    boxShadow: 1,
                    flexShrink: 0,
                    minWidth: 120,
                    display:'none'
                  }}
                >
                  Refresh Details
                </Button>
              </Box>
              <Box display="flex" justifyContent="space-between" flexWrap="wrap" mb={3}>
                <Box width={{ xs: '100%', md: '50%' }} textAlign="center">
                  <Typography variant="h5" color="secondary">Risk Level</Typography>
                  <GaugeChart
                    id="gauge-semi"
                    nrOfLevels={10}
                    colors={[
                        '#4575b4', '#4575b4', '#74add1',
                        '#abd9e9', '#fddbc7', '#fdae61',
                        '#f46d43', '#d73027', '#a50026',
                        '#a50026'
                    ]}
                    arcWidth={0.3}
                    percent={percentValue}
                    needleColor="#37474F"          
                    needleBaseColor="#212121"     
                    textColor="#333333"
                    animate={true}
                    animDelay={0}
                    formatTextValue={(value) => `${finalScore}`}
                    style={{ width: '100%' }}
                  />
                  <Tooltip title={scoreDetails} arrow placement="top">
                    <Typography
                      variant="h6"
                      color={getRiskLevel() === 'High Risk' ? '#a50026' : getRiskLevel() === 'Medium Risk' ? '#fdae61' : '#4575b4'}
                      fontWeight={700}
                      mt={2}
                    >
                      {getRiskLevel()}
                    </Typography>
                  </Tooltip>
                </Box>
                <Box
                  width={{ xs: '100%', md: '48%' }}
                  display="flex"
                  flexDirection="column"
                  sx={{ mt: { xs: 2, md: 0 } }} // 16px margin-top for mobile only
                >
                  <Button
                    variant="text"
                    fullWidth
                    onClick={() => scrollToRef(googleReviewsRef)}
                    sx={{
                      fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                      py: { xs: 0.8, md: 1.2 },
                      mb: { xs: 0, md: 1 }
                    }}
                  >
                    Google Reviews Analysis
                  </Button>
                  <Button
                    variant="text"
                    fullWidth
                    onClick={() => scrollToRef(crimeRadiusRef)}
                    sx={{
                      fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                      py: { xs: 0.8, md: 1.2 },
                      mb: { xs: 0, md: 1.5 }
                    }}
                  >
                    Crime Radius
                  </Button>
                  {/* <Button
                    variant="text"
                    fullWidth
                    onClick={() => scrollToRef(crimeRateRef)}
                    sx={{
                      fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                      py: { xs: 0.8, md: 1.2 },
                      mb: { xs: 0, md: 1.5 }
                    }}
                  >
                    Crime Rate
                  </Button> */}
                  {/* <Button
                    variant="text"
                    fullWidth
                    onClick={() => scrollToRef(zipCrimeRef)}
                    sx={{
                      fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                      py: { xs: 0.8, md: 1.2 },
                      mb: { xs: 0, md: 1.5 }
                    }}
                  >
                    Zip Code Crime Grade
                  </Button> */}
                  <Button
                    variant="text"
                    fullWidth
                    onClick={() => scrollToRef(socioRef)}
                    sx={{
                      fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                      py: { xs: 0.8, md: 1.2 },
                      mb: { xs: 0, md: 1.5 }
                    }}
                  >
                    Local Socioeconomic Conditions
                  </Button>
                  <Button
                    variant="text"
                    fullWidth
                    onClick={() => scrollToRef(businessRef)}
                    sx={{
                      fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                      py: { xs: 0.8, md: 1.2 },
                      mb: { xs: 0, md: 1.5 }
                    }}
                  >
                    Nearby Business Risks
                  </Button>
                  {/* <Button
                    variant="text"
                    fullWidth
                    onClick={() => scrollToRef(sexOffenseRef)}
                    sx={{
                      fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                      py: { xs: 0.8, md: 1.2 }
                    }}
                  >
                    Sexual Offense
                  </Button> */}
                </Box>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
                mb={2}
                py={2}
              >
                <Typography
                  variant="h6"
                  color="secondary"
                  sx={{
                    mb: { xs: 0, sm: 0 },
                    textAlign: { xs: 'left', sm: 'inherit' }
                  }}
                >
                  Risk & Community Scores
                </Typography>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleToggleExpandAll}
                  sx={{
                    flexShrink: 0,
                    minWidth: 120,
                  }}
                >
                  {expandAll ? 'Collapse All' : 'Expand All'}
                </Button>
              </Box>

              <Box
                flex={2}
                display="flex"
                flexDirection="column"
                gap={{ xs: 1, md: 3 }}
                pr={{ xs: 0, md: 3 }}
                pl={{ xs: 0, md: 0 }}
                mt={2}
                pb={4}
                mr={{ xs: 2, md: 0 }}
              >
                <Box ref={googleReviewsRef}>
                  <Ratings score={scores.googleReviews} title="Google Reviews Analysis" reviews={propertyDetails.GoogleReviewResponse} card="1" expandAll={expandAll} placeDetails={placeDetails} />
                </Box>
                {/* <Box ref={crimeRateRef}>
                  <Ratings score={scores.crimeRadius} title="Crime Rate" reviews={propertyDetails.CrimeRateResponse} card="7" expandAll={expandAll} placeDetails={placeDetails} />
                </Box> */}
                <Box ref={crimeRadiusRef}>
                  <Ratings score={scores.crimeRadius} title="Crime Radius" reviews={propertyDetails.CrimeRadiusResponse} card="2" expandAll={expandAll} placeDetails={placeDetails} />
                </Box>
                {/* <Box ref={zipCrimeRef}>
                  <Ratings score={scores.zipCodeCrimeGrade} title="Zip Code Crime Grade" reviews={propertyDetails.ZipCodeResponse} card="3" expandAll={expandAll} placeDetails={placeDetails} />
                </Box> */}
                <Box ref={socioRef}>
                  <Ratings score={scores.localSocialConditions} title="Local Socioeconomic Conditions" reviews={propertyDetails.SocioEconomicsResponse} card="4" expandAll={expandAll} placeDetails={placeDetails} />
                </Box>
                <Box ref={businessRef}>
                  <Ratings score={scores.nearbyBusinessRisks} title="Nearby Business Risks" reviews={propertyDetails.BusinessRisksResponse} card="5" expandAll={expandAll} placeDetails={placeDetails} />
                </Box>
                
                {/* <Box ref={sexOffenseRef}>
                  <Ratings score={scores.sexOffenders} title="Sexual Offense" reviews={propertyDetails.SexOffendersResponse} card="6" expandAll={expandAll} placeDetails={placeDetails} />
                </Box> */}
              </Box>
            </Box>
          </Box>
        )}
        {loading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress color="secondary" />
          </Box>
        )}
      </Paper>
      <Dialog open={historyOpen} onClose={handleHistoryClose} maxWidth="md" fullWidth>
        <DialogTitle>Property History</DialogTitle>
        <DialogContent>
          <List sx={{ listStyleType: 'disc', pl: 3, maxHeight: 10 * 48, overflowY: 'auto' }}>
            {historyList.length > 0 ?
              (historyList
                .sort((a, b) => (a.PropertyName || '').localeCompare(b.PropertyName || ''))
                .map((property, idx) => (
                  <ListItem key={idx} disablePadding sx={{ display: 'list-item' }}>
                    <ListItemButton onClick={() => handleHistoryClick(property)}>
                      <ListItemText primary={property.PropertyName} style={{ color: '#1976d2' }} />
                    </ListItemButton>
                  </ListItem>
                ))
              )
              :
              (
                <ListItem>
                  <ListItemText primary="No properties found." />
                </ListItem>
              )
            }
          </List>
        </DialogContent>
      </Dialog>
    </Container>
  );
}

export default Dashboard;
